import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip
} from '@mui/material';
import {
  Church as ChurchIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarIcon,
  EuroSymbol as IntentionIcon,
  PersonAdd as ClergyIcon
} from '@mui/icons-material';

const MassManagement = () => {
  const managementCards = [
    {
      title: 'Mass Schedule',
      description: 'View and manage Mass schedules and liturgical calendar',
      icon: <CalendarIcon sx={{ fontSize: 48, color: '#1976d2' }} />,
      path: '/mass/schedule',
      color: '#e3f2fd',
      comingSoon: true
    },
    {
      title: 'Mass Intentions',
      description: 'Manage Mass intentions and track payments',
      icon: <IntentionIcon sx={{ fontSize: 48, color: '#388e3c' }} />,
      path: '/mass/intentions',
      color: '#e8f5e8',
      comingSoon: true
    },
    {
      title: 'Clergy Management',
      description: 'Manage clergy assignments and liturgical roles',
      icon: <ClergyIcon sx={{ fontSize: 48, color: '#f57c00' }} />,
      path: '/mass/clergy',
      color: '#fff3e0',
      comingSoon: true
    },
    {
      title: 'Contact Directory',
      description: 'Manage parishioner contacts for Mass intentions',
      icon: <PeopleIcon sx={{ fontSize: 48, color: '#7b1fa2' }} />,
      path: '/mass/contacts',
      color: '#f3e5f5',
      comingSoon: true
    },
    {
      title: 'Liturgical Calendar',
      description: 'Import and manage liturgical calendar data',
      icon: <ChurchIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
      path: '/mass/liturgical',
      color: '#ffebee',
      comingSoon: true
    },
    {
      title: 'Mass Settings',
      description: 'Configure Mass schedules and intention settings',
      icon: <SettingsIcon sx={{ fontSize: 48, color: '#455a64' }} />,
      path: '/mass/settings',
      color: '#eceff1',
      comingSoon: true
    }
  ];

  const handleCardClick = (path, comingSoon) => {
    if (comingSoon) {
      alert('This feature is coming soon!');
      return;
    }
    // Navigation logic would go here
    console.log(`Navigate to: ${path}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #8e24aa 0%, #ba68c8 100%)', color: 'white' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Mass Management System
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Comprehensive management of Mass schedules, intentions, clergy assignments, and liturgical calendar integration.
        </Typography>
      </Paper>

      {/* Management Cards */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
        System Components
      </Typography>

      <Grid container spacing={3} sx={{ mb: 5 }}>
        {managementCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                },
                position: 'relative'
              }}
            >
              <CardActionArea
                onClick={() => handleCardClick(card.path, card.comingSoon)}
                sx={{ height: '100%', p: 2 }}
              >
                {card.comingSoon && (
                  <Chip
                    label="Coming Soon"
                    color="secondary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1
                    }}
                  />
                )}
                <CardContent sx={{ textAlign: 'center', height: '100%' }}>
                  <Box
                    sx={{
                      backgroundColor: card.color,
                      borderRadius: '50%',
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px auto'
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* System Overview */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          System Overview
        </Typography>
        <Typography variant="body1" paragraph>
          The Mass Management System provides comprehensive tools for managing all aspects of parish Mass schedules and intentions:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Key Features:
            </Typography>
            <ul style={{ marginTop: 0 }}>
              <li>Liturgical calendar integration with automatic data import</li>
              <li>Mass scheduling based on liturgical year cycles</li>
              <li>Intention management with payment tracking</li>
              <li>Clergy assignment with proper liturgical roles</li>
              <li>Contact management for parishioners</li>
              <li>Monthly calendar export and printing</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Liturgical Integration:
            </Typography>
            <ul style={{ marginTop: 0 }}>
              <li>Advent to Advent liturgical year support</li>
              <li>Automatic liturgical season and color tracking</li>
              <li>Proper Catholic Mass structure enforcement</li>
              <li>Scripture reading references</li>
              <li>Feast day and memorial recognition</li>
              <li>US Catholic calendar compliance</li>
            </ul>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            <strong>Implementation Status:</strong> The Mass Management System backend is fully implemented with all models,
            API routes, and services ready. Frontend components are currently in development and will be available soon.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default MassManagement;